// src/hooks/useScrollIntoViewOnOpen.js
//
// Small, reusable scroll-into-view mechanism for "expand in place" UI — the companion to
// useExpandInPlace.js, which deliberately does NOT own this (see its own header comment).
//
// Pattern imitated from ConcertinaThread.jsx's openThread() (its own threadRefs ref-registry +
// a setTimeout'd scrollIntoView({ block: 'nearest', behavior: 'smooth' })) — reimplemented here
// standalone so it isn't entangled with that engine's data-fetching/composer/permalink/discard
// logic. First consumer: the Events MiniCard
// (docs/briefs/2026-08-15-events-minicard-build-1.md). Built to be lifted into Build 2 and the
// later News/Directory phases without change — do not fold consumer-specific logic into this file.
//
// Usage:
//   const { openId, isOpen, toggle } = useExpandInPlace();
//   const { setRef } = useScrollIntoViewOnOpen(openId);
//   <div ref={setRef(item.id)}>...</div>
//
// The delay exists because the just-opened content (e.g. a SlideDown reflow) needs a frame to
// take its final height before scrollIntoView measures the right position — same reasoning as
// ConcertinaThread's own 100ms delay.

import { useRef, useCallback, useEffect } from 'react';

export function useScrollIntoViewOnOpen(openId, options = {}) {
  const { block = 'nearest', behavior = 'smooth', delay = 100 } = options;
  const nodeRefs = useRef({});

  const setRef = useCallback((id) => (node) => {
    if (node) {
      nodeRefs.current[id] = node;
    } else {
      delete nodeRefs.current[id];
    }
  }, []);

  useEffect(() => {
    if (!openId) return;
    const timer = setTimeout(() => {
      nodeRefs.current[openId]?.scrollIntoView({ block, behavior });
    }, delay);
    return () => clearTimeout(timer);
  }, [openId, block, behavior, delay]);

  return { setRef };
}
